import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button, ErrorNote, Field, inputClass, Loading } from '../components/ui';
import { api, ApiError, authRequest, signOut, unwrap } from '../lib/api';
import { fmtDate } from '../lib/format';
import { useInvitation, useMe, useSignInMethods } from '../lib/queries';
import { AuthShell, ProviderButtons } from './SignIn';

export function Invite() {
  const { id = '' } = useParams();
  const invitation = useInvitation(id);
  const me = useMe();
  const methods = useSignInMethods();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const accept = useMutation({
    mutationFn: () => unwrap(api.POST('/invitations/{id}/accept', { params: { path: { id } } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      navigate('/', { replace: true });
    },
  });

  if (invitation.isPending || me.isPending) return <AuthShell title="Invitation"><Loading /></AuthShell>;
  if (invitation.error) {
    return <AuthShell title="Invitation"><ErrorNote error={invitation.error instanceof ApiError && invitation.error.status === 404 ? new Error('This invitation link is not valid.') : invitation.error} /></AuthShell>;
  }
  const inv = invitation.data;
  if (inv.status !== 'pending') {
    const why = inv.status === 'accepted' ? 'has already been used' : inv.status === 'expired' ? 'has expired' : 'was canceled';
    return <AuthShell title="Invitation"><p className="text-sm">This invitation {why}. Ask your admin for a new one.</p></AuthShell>;
  }

  const roleText = inv.role === 'admin' ? 'an admin (all plants, members and devices)' : 'a viewer';
  return (
    <AuthShell title={`Join ${inv.organization}`}>
      <p className="mb-4 text-sm">
        You have been invited to {inv.organization} as {roleText}. The invitation is for <span className="font-medium">{inv.email}</span> and is valid until {fmtDate(inv.expiresAt, Intl.DateTimeFormat().resolvedOptions().timeZone)}.
      </p>
      {me.data ? (
        <>
          <p className="mb-3 text-sm text-muted">Signed in as {me.data.user.email}.</p>
          <ErrorNote error={accept.error} />
          <div className="mt-3 flex gap-2">
            <Button variant="primary" onClick={() => accept.mutate()} disabled={accept.isPending}>Accept invitation</Button>
            {accept.error && (
              <Button onClick={async () => { await signOut(); await queryClient.invalidateQueries(); }}>Sign in as someone else</Button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="mb-3 text-sm text-muted">Sign in with the Microsoft or Google account for that address.</p>
          <ProviderButtons next={`/invite/${id}`} />
          {methods.data?.password && <CreateAccount />}
        </>
      )}
    </AuthShell>
  );
}

/** Local development only: create the invited account with a password. */
function CreateAccount() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const queryClient = useQueryClient();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await authRequest('/sign-up/email', { email, name, password });
      await queryClient.invalidateQueries();
    } catch (err) {
      setError(err);
    }
  };
  return (
    <form onSubmit={submit} className="mt-5 border-t border-rule pt-4">
      <div className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">Local development: create a password account</div>
      <Field label="Email (the invited address)"><input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
      <Field label="Name"><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required /></Field>
      <Field label="Password" hint="At least 8 characters"><input className={inputClass} type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required /></Field>
      <ErrorNote error={error} />
      <Button type="submit" className="mt-2 w-full">Create account</Button>
    </form>
  );
}
